import { IAccessPoint } from '../access-point/access-point.interface';
import { IBaseModel, IBaseModelWoId } from '../base/base-model.interface';
import { createEnumOptions } from '../shared/enum-option.interface';

export enum OrderState {
  accepted = 'accepted',
  configured = 'configured',
  documentationProcess = 'documentationProcess',
  finished = 'finished',
  rejectedInvalidOrder = 'rejectedInvalidOrder',
  rejectedNotAvailable = 'rejectedNotAvailable',
  canceled = 'canceled',
  closed = 'closed',
}

export const orderClosedStates = [
  OrderState.rejectedInvalidOrder,
  OrderState.rejectedNotAvailable,
  OrderState.canceled,
  OrderState.closed,
];

export const orderActiveStates = [
  OrderState.accepted,
  OrderState.configured,
  OrderState.documentationProcess,
  OrderState.finished,
];

export const orderActive = (state: OrderState) =>
  orderActiveStates.includes(state);

export const orderClosed = (state: OrderState) =>
  orderClosedStates.includes(state);

export enum InstallationState {
  pendingConnectionInProgress = 'pendingConnectionInProgress',
  assignedAssignedToPartner = 'assignedAssignedToPartner',
  assignedPlanned = 'assignedPlanned',
  assignedInstallationInProgress = 'assignedInstallationInProgress',
  parkedUnableContactCustomer = 'parkedUnableContactCustomer',
  parkedCustomerNotHome = 'parkedCustomerNotHome',
  parkedDeferredInstallation = 'parkedDeferredInstallation',
  parkedNonstandardConnection = 'parkedNonstandardConnection',
  canceledWoDispatchUnsuccessfulContact = 'canceledWoDispatchUnsuccessfulContact',
  canceledWoDispatchCustomerRefused = 'canceledWoDispatchCustomerRefused',
  canceledWoDispatchIncreasedCosts = 'canceledWoDispatchIncreasedCosts',
  canceledWoDispatchNoLongerInterested = 'canceledWoDispatchNoLongerInterested',
  canceledWoDispatchOccupiedAddress = 'canceledWoDispatchOccupiedAddress',
  canceledWDispatchAddressOutOfRange = 'canceledWDispatchAddressOutOfRange',
  canceledWDispatchUnfeasibleInstallation = 'canceledWDispatchUnfeasibleInstallation',
  canceledWDispatchCustomerRefused = 'canceledWDispatchCustomerRefused',
  canceledWDispatchIncreasedCosts = 'canceledWDispatchIncreasedCosts',
  canceledWDispatchNoLongerInterested = 'canceledWDispatchNoLongerInterested',
  installed = 'installed',
}

export enum ActivationState {
  missingParams = 0,
  available = 1,
  activating = 2,
  failed = 3,
  activated = 4,
  conflict = 5,
}

export enum OrderActions {
  changeOnt = 'changeOnt',
  cancel = 'cancel',
  reconfigure = 'reconfigure',
  suspend = 'suspend',
  unsuspend = 'unsuspend',
  disconnection = 'disconnection',
  setInstallationState = 'setInstallationState',
  reopen = 'reopen',
  deleteOldOnts = 'deleteOldOnts',
  changeSpeedProfiles = 'changeSpeedProfiles',
}

export const orderMassActionOptions = createEnumOptions({
  [OrderActions.changeSpeedProfiles]: {
    label: 'Zmena rýchlostných profilov',
  },
  [OrderActions.cancel]: { label: 'Ukončiť objednávky' },
  [OrderActions.reconfigure]: {
    label: 'Opakovane nakonfigurovať ONT',
  },
  _default: { label: '-' },
});

export interface IOrderMassActionData {
  action: OrderActions;
  speedProfileUp?: string;
  speedProfileDown?: string;
  closeDate?: Date;
}

export enum OrderCloseReason {
  isp = 'isp',
  admin = 'admin',
  limits = 'limits',
  migration = 'migration',
}

export interface IOrderInstallation {
  id: string;
  name?: string;
  surname?: string;
  phone?: string;
  email?: string;
  paragraph?: string;
  startDate?: Date;
  endDate?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IOrder extends IBaseModel<'Order'> {
  apId?: string | null;
  eic?: string;
  address?: string;

  extOrderId?: string;
  extCustomerId?: string;
  dopInstall?: boolean;

  state?: OrderState;
  installationState?: InstallationState;
  startDate?: Date;
  closeDate?: Date;
  activationDate?: Date;
  closeReason?: OrderCloseReason;

  ontSn?: string;
  tierL2?: boolean;
  ontType?: string | null;
  speedProfileUp?: string;
  speedProfileDown?: string;
  serviceProfile?: string;
  lineProfile?: string;
  servicePortsSpecs?: string | null;

  firstName?: string;
  lastName?: string;
  companyName?: string;
  phone?: string;

  groupId?: string;
  activationState?: ActivationState;
  installation?: IOrderInstallation;

  remoteId?: string;
  circuitId?: string;

  migratedAt?: Date;

  accessPoint?: IAccessPoint;
}

export interface IOrderActionData {
  id: string;
  apId?: string | null | undefined;
  ontSn?: string;
  noDowntime?: boolean;
  closeDate?: Date;
  installationState?: InstallationState;
  extOrderId?: string;
  ontType?: string | null;
  speedProfileUp?: string;
  speedProfileDown?: string;
  serviceProfile?: string;
  remoteId?: string;
  circuitId?: string;
  servicePortsSpecs?: string | null;
}

export const orderStateOptions = createEnumOptions<OrderState>({
  [OrderState.accepted]: { label: 'Prijatá' },
  [OrderState.configured]: { label: 'Nakonfigurovaná' },
  [OrderState.documentationProcess]: { label: 'Proces dokumentácie' },
  [OrderState.finished]: { label: 'Dokončená' },
  [OrderState.rejectedInvalidOrder]: {
    label: 'Zamietnutá - neplatná objednávka',
  },
  [OrderState.rejectedNotAvailable]: { label: 'Zamietnutá - nedostupné' },
  [OrderState.canceled]: { label: 'Zrušená' },
  [OrderState.closed]: { label: 'Ukončená' },
  _default: { label: '-' },
});

export interface IOrderSettings extends IBaseModelWoId<'Group'> {
  groupId: string;
  allowRestrictions: boolean;
  installDays: number;
  nonDocCount: number;
  validFrom: Date;
  sendEmailOnNonDocumentedOrder: boolean;
  nonDocumentedOrderDays: number;
}

export const installationStateOptions = createEnumOptions<InstallationState>({
  [InstallationState.pendingConnectionInProgress]: {
    label: 'Čaká na inštaláciu',
  },
  [InstallationState.assignedAssignedToPartner]: {
    label: 'Priradená servisnému partnerovi',
  },
  [InstallationState.assignedPlanned]: { label: 'Zákaznik naplánovaný' },
  [InstallationState.assignedInstallationInProgress]: {
    label: 'Prebieha inštalácia',
  },
  [InstallationState.parkedUnableContactCustomer]: {
    label: 'Zákazník nedostupný',
  },
  [InstallationState.parkedCustomerNotHome]: {
    label: 'Zákazník nebol doma v dohodnutom čase',
  },
  [InstallationState.parkedDeferredInstallation]: {
    label: 'Zákazník požiadal o neskorší termín',
  },
  [InstallationState.parkedNonstandardConnection]: {
    label: 'Neštandardné riešenie prípojky',
  },
  [InstallationState.canceledWoDispatchUnsuccessfulContact]: {
    label: 'Zrušená bez výjazdu - Opakovane sa nepodarilo spojiť so zákazníkom',
  },
  [InstallationState.canceledWoDispatchCustomerRefused]: {
    label: 'Zrušená bez výjazdu - Odmieta zásahy do nehnuteľnosti',
  },
  [InstallationState.canceledWoDispatchIncreasedCosts]: {
    label:
      'Zrušená bez výjazdu - Odmieta z dôvodu navýšenia nákladov na prípojku',
  },
  [InstallationState.canceledWoDispatchNoLongerInterested]: {
    label: 'Zrušená bez výjazdu - Nemá už záujem o službu',
  },
  [InstallationState.canceledWoDispatchOccupiedAddress]: {
    label:
      'Zrušená bez výjazdu - Adresa je obsadená napriek statusu v online checkeri',
  },
  [InstallationState.canceledWDispatchAddressOutOfRange]: {
    label: 'Zrušená s výjazdom - Prípojka je mimo dosahu optickej siete',
  },
  [InstallationState.canceledWDispatchUnfeasibleInstallation]: {
    label: 'Zrušená s výjazdom - Prípojku je technicky nemožné realizovať',
  },
  [InstallationState.canceledWDispatchCustomerRefused]: {
    label: 'Zrušená s výjazdom - Odmieta zásahy do nehnuteľnosti',
  },
  [InstallationState.canceledWDispatchIncreasedCosts]: {
    label:
      'Zrušená s výjazdom - Odmieta z dôvodu navýšenia nákladov na prípojku',
  },
  [InstallationState.canceledWDispatchNoLongerInterested]: {
    label: 'Zrušená s výjazdom - Nemá už záujem o službu',
  },
  [InstallationState.installed]: { label: 'Nainštalovaná' },
  _default: { label: '-' },
});

export const orderActivationStateOptions = createEnumOptions<ActivationState>({
  [ActivationState.missingParams]: { label: 'Chýbajúce údaje' },
  [ActivationState.available]: { label: 'Pripravené' },
  [ActivationState.activating]: { label: 'Konfiguruje sa' },
  [ActivationState.failed]: { label: 'Konfigurácia zlyhala' },
  [ActivationState.activated]: {
    label: 'Konfigurácia úspešná',
  },
  [ActivationState.conflict]: { label: 'Konflikt' },
  _default: { label: '-' },
});

export const orderCloseReasonOptions = createEnumOptions<OrderCloseReason>({
  [OrderCloseReason.admin]: { label: 'Správca OAN' },
  [OrderCloseReason.isp]: { label: 'ISP' },
  [OrderCloseReason.limits]: { label: 'Prekročenie limitov' },
  [OrderCloseReason.migration]: { label: 'Migrácia' },
  _default: { label: '-' },
});

export const docFields = [
  'usedOdb',
  'dopPort',
  'dopType',
  'dopRoad',
  'dopDbmSignal',
  'dopPonmeterPhoto',
  'dopPhoto',
  'dopRoadPhoto',
  'dopRoadAfterPhoto',
  'endpointType',
  'odbBeforePhoto',
  'odbAfterPhoto',
] as const;

export type DocField = (typeof docFields)[number] & keyof IAccessPoint;

export interface IDocFieldConfig {
  label?: string;
  disabled?: boolean;
  mandatory?: boolean;
}

export interface IDocFieldsConfig {
  date: Date;
  fields: Partial<Record<DocField, IDocFieldConfig>>;
}

export const DOC_FIELDS_V1_DATE = new Date(0);
export const DOC_FIELDS_V2_DATE = new Date(2026, 1, 0);
export const DOC_FIELDS_V3_DATE = new Date(2999, 2, 0);

export const DOC_FIELDS_V1: IDocFieldsConfig = {
  date: DOC_FIELDS_V1_DATE,
  fields: {
    usedOdb: {
      label: 'Použitý ODB',
      mandatory: false,
    },
    dopPort: {
      label: 'ODB port',
      mandatory: true,
    },
    dopType: {
      label: 'DOP typ',
      mandatory: true,
    },
    dopRoad: {
      label: 'DOP trasa',
      mandatory: true,
    },
    dopDbmSignal: {
      label: 'DOP dBm signál',
      mandatory: false,
    },
    endpointType: {
      label: 'Typ ukončenia DOP',
      mandatory: false,
    },
    dopPhoto: {
      label: 'Foto DOP',
      mandatory: true,
    },
    dopRoadPhoto: {
      label: 'Foto DOP trasa',
      mandatory: true,
    },
    dopPonmeterPhoto: {
      label: 'Foto DOP PONmeter report',
      mandatory: false,
    },
  },
};

export const DOC_FIELDS_V2: IDocFieldsConfig = {
  date: DOC_FIELDS_V2_DATE,
  fields: {
    usedOdb: {
      label: 'Použitý ODB',
      mandatory: false,
    },
    dopPort: {
      label: 'ODB port',
      mandatory: true,
    },
    dopType: {
      label: 'DOP typ',
      mandatory: true,
    },
    dopRoad: {
      label: 'DOP trasa',
      mandatory: true,
    },
    dopDbmSignal: {
      label: 'DOP dBm signál',
      mandatory: false,
    },
    endpointType: {
      label: 'Typ ukončenia DOP',
      mandatory: false,
    },
    dopPhoto: {
      label: 'Foto DOP',
      mandatory: true,
    },
    dopRoadPhoto: {
      label: 'Stĺp pred zhotovením prípojky',
      mandatory: true,
    },
    dopRoadAfterPhoto: {
      label: 'Stĺp po zhotovení prípojky',
      mandatory: false,
    },
    dopPonmeterPhoto: {
      label: 'Foto DOP PONmeter report',
      mandatory: false,
    },
    odbBeforePhoto: {
      label: 'ODB pred zhotovením prípojky',
      mandatory: false,
    },
    odbAfterPhoto: {
      label: 'ODB po zhotovení prípojky',
      mandatory: false,
    },
  },
};

export const DOC_FIELDS_V3: IDocFieldsConfig = {
  date: DOC_FIELDS_V3_DATE,
  fields: {
    usedOdb: {
      ...DOC_FIELDS_V1.fields.usedOdb,
      mandatory: false,
    },
    dopPort: {
      ...DOC_FIELDS_V1.fields.dopPort,
      mandatory: true,
    },
    dopType: {
      ...DOC_FIELDS_V1.fields.dopType,
      mandatory: true,
    },
    dopDbmSignal: {
      ...DOC_FIELDS_V1.fields.dopDbmSignal,
      mandatory: true,
    },
    dopPhoto: {
      ...DOC_FIELDS_V1.fields.dopPhoto,
      disabled: true,
      mandatory: false,
    },
    dopRoadPhoto: {
      ...DOC_FIELDS_V2.fields.dopRoadPhoto,
      mandatory: true,
    },
    dopRoadAfterPhoto: {
      ...DOC_FIELDS_V2.fields.dopRoadAfterPhoto,
      mandatory: true,
    },
    dopPonmeterPhoto: {
      ...DOC_FIELDS_V1.fields.dopPonmeterPhoto,
      mandatory: true,
    },
    odbBeforePhoto: {
      ...DOC_FIELDS_V2.fields.odbBeforePhoto,
      mandatory: true,
    },
    odbAfterPhoto: {
      ...DOC_FIELDS_V2.fields.odbAfterPhoto,
      mandatory: true,
    },
  },
};

// export const docFieldsConfig: Record<DocField, IDocFieldConfig> = {
//   usedOdb: {},
//   dopPort: {
//     mandatoryFrom: DOC_FIELDS_V1,
//   },
//   dopType: {
//     mandatoryFrom: DOC_FIELDS_V1,
//   },
//   dopRoad: {
//     mandatoryFrom: DOC_FIELDS_V1,
//     removedFrom: DOC_FIELDS_V2,
//   },
//   dopDbmSignal: {
//     mandatoryFrom: DOC_FIELDS_V1,
//   },
//   dopPonmeterPhoto: {
//     mandatoryFrom: DOC_FIELDS_V1,
//   },
//   dopPhoto: {
//     mandatoryFrom: DOC_FIELDS_V1,
//     disabledFrom: DOC_FIELDS_V2,
//   },
//   dopRoadPhoto: {
//     mandatoryFrom: DOC_FIELDS_V1,
//   },
//   dopRoadAfterPhoto: {
//     mandatoryFrom: DOC_FIELDS_V2,
//   },
//   endpointType: {
//     removedFrom: DOC_FIELDS_V2,
//   },
//   odbBeforePhoto: {
//     mandatoryFrom: DOC_FIELDS_V2,
//   },
//   odbAfterPhoto: {
//     mandatoryFrom: DOC_FIELDS_V2,
//   },
// };

export const DOC_FIELD_VERSIONS: IDocFieldsConfig[] = [
  DOC_FIELDS_V1,
  DOC_FIELDS_V2,
  DOC_FIELDS_V3,
];

export const getDocFieldsConfig = (date: Date): IDocFieldsConfig => {
  const applicableConfig = DOC_FIELD_VERSIONS.sort(
    (a, b) => b.date.getTime() - a.date.getTime(),
  ).find((config) => date >= config.date);

  return applicableConfig || DOC_FIELDS_V1;
};

export const mandatoryDocFields = (orderStartDate: Date) => {
  const config = getDocFieldsConfig(orderStartDate);

  return Object.entries(config.fields)
    .filter(([_, fieldConfig]) => fieldConfig.mandatory)
    .map(([field]) => field as DocField);
};
